// Analýza → Do košíku: poměr přidání do košíku po produktech (GA4).
// Celý produkt = všechny velikosti, CZ i SK dohromady.

import { CartRateTrendModal, type TrendProduct } from '@/components/analytics/CartRateTrendModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useMemo, useState } from 'react';

type Range = { startDate: string; endDate: string };

type CartRateProduct = {
  key: string;
  code: string | null;
  name: string;
  image_url: string | null;
  url: string | null;
  views: number;
  adds: number;
  rate: number | null;
};

type CartRateResponse = {
  totals: { views: number; adds: number };
  /** Kód produktu → v kolika upsell nabídkách / sadách se nabízí jinde. */
  recommended?: Record<string, number>;
  /** SK e-shop zatím nemá GA4 property — „CZ + SK" ukazuje jen CZ. */
  skMissing?: boolean;
  products: CartRateProduct[];
};

type SortKey = 'views' | 'adds' | 'rate';
type Eshop = 'cz' | 'sk' | 'all';

const ESHOPY: { key: Eshop; label: string }[] = [
  { key: 'all', label: 'CZ + SK' },
  { key: 'cz', label: 'CZ' },
  { key: 'sk', label: 'SK' },
];

const PAGE_SIZE = 50;
// Pod tímhle počtem zobrazení je poměr spíš náhoda — řádek se zašedí.
const MIN_VIEWS_DEFAULT = 50;

const fmtNum = (n: number) => new Intl.NumberFormat('cs-CZ').format(Math.round(n));
const fmtPct = (n: number | null) => (n == null ? '–' : `${n.toFixed(1).replace('.', ',')} %`);

export function CartRateTab({ range }: { range: Range }) {
  const [data, setData] = useState<CartRateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [minViews, setMinViews] = useState(MIN_VIEWS_DEFAULT);
  const [jenSpolehlive, setJenSpolehlive] = useState(true);
  const [sortBy, setSortBy] = useState<SortKey>('views');
  const [desc, setDesc] = useState(true);
  const [page, setPage] = useState(0);
  const [eshop, setEshop] = useState<Eshop>('all');
  const [detail, setDetail] = useState<TrendProduct | null>(null);

  useEffect(() => {
    let zruseno = false;
    setLoading(true);
    setErr(null);
    setData(null);
    const params = new URLSearchParams({ from: range.startDate, to: range.endDate, eshop });
    apiFetch<CartRateResponse>(`/api/analytics/cart-rate?${params.toString()}`)
      .then((r) => {
        if (!zruseno) setData(r);
      })
      .catch((e: unknown) => {
        if (!zruseno) setErr(e instanceof ApiError ? e.describe() : 'Načtení selhalo');
      })
      .finally(() => {
        if (!zruseno) setLoading(false);
      });
    return () => {
      zruseno = true;
    };
  }, [range.startDate, range.endDate, eshop]);

  // Změna filtru / řazení → zpět na první stranu.
  // biome-ignore lint/correctness/useExhaustiveDependencies: závislosti jsou spouštěče, ne vstupy
  useEffect(() => setPage(0), [q, minViews, jenSpolehlive, sortBy, desc]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = (data?.products ?? []).filter(
      (p) =>
        p.views > 0 &&
        (!jenSpolehlive || p.views >= minViews) &&
        (!s || p.name.toLowerCase().includes(s) || (p.code ?? '').toLowerCase().includes(s)),
    );
    const val = (p: CartRateProduct) => (sortBy === 'rate' ? (p.rate ?? -1) : p[sortBy]);
    return list.sort((a, b) => (desc ? val(b) - val(a) : val(a) - val(b)));
  }, [data, q, minViews, jenSpolehlive, sortBy, desc]);

  function toggleSort(k: SortKey) {
    if (sortBy === k) setDesc(!desc);
    else {
      setSortBy(k);
      setDesc(true);
    }
  }

  const avgRate =
    data && data.totals.views > 0 ? (data.totals.adds / data.totals.views) * 100 : null;
  const pageCount = Math.max(Math.ceil(rows.length / PAGE_SIZE), 1);
  const visible = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const sortHead = (k: SortKey, label: string) => (
    <button
      type="button"
      onClick={() => toggleSort(k)}
      className="inline-flex items-center gap-1 hover:text-text cursor-pointer ml-auto"
    >
      {label}
      <span className="text-[10px] opacity-70">{sortBy === k ? (desc ? '▼' : '▲') : '↕'}</span>
    </button>
  );

  return (
    <div className="bg-surface rounded-xl shadow-card">
      <div className="flex items-start justify-between px-5 pt-5 pb-3 flex-wrap gap-3">
        <div>
          <h2 className="text-base font-bold text-text">Přidání do košíku po produktech</h2>
          <p className="text-[11px] text-text-muted mt-0.5">
            Poměr = přidání do košíku ÷ zobrazení detailu (GA4), za celý produkt (všechny
            velikosti). Kliknutím na produkt otevřeš týdenní trend.
            {avgRate != null && (
              <>
                {' '}
                Průměr e-shopu: <strong className="text-text">{fmtPct(avgRate)}</strong>
              </>
            )}
          </p>
          {data?.skMissing && eshop === 'all' && (
            <p className="text-[11px] text-warning mt-0.5">
              SK zatím není napojené na GA4 — čísla jsou jen za CZ.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-bg border border-border rounded-lg overflow-hidden">
            {ESHOPY.map((e) => (
              <button
                key={e.key}
                type="button"
                onClick={() => setEshop(e.key)}
                className={`text-xs font-medium px-3 py-1.5 cursor-pointer transition-colors ${
                  eshop === e.key
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:bg-surface-hover'
                }`}
              >
                {e.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat produkt / kód…"
            className="w-56 border border-border rounded-lg px-3 py-1.5 text-sm text-text focus:outline-none focus:border-primary"
          />
          <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={jenSpolehlive}
              onChange={(e) => setJenSpolehlive(e.target.checked)}
            />
            jen s aspoň
            <input
              type="number"
              min={1}
              value={minViews}
              onChange={(e) => setMinViews(Math.max(1, Number(e.target.value) || 1))}
              className="w-16 border border-border rounded px-2 py-1 text-xs text-text"
            />
            zobrazeními
          </label>
        </div>
      </div>
      {err && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded mx-5 mb-3 px-4 py-3">
          {err}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg border-b border-border text-text-secondary">
            <tr>
              <th className="text-left font-semibold px-4 py-2.5">Produkt</th>
              <th className="text-right font-semibold px-4 py-2.5 w-32">
                {sortHead('views', 'Zobrazení')}
              </th>
              <th className="text-right font-semibold px-4 py-2.5 w-32">
                {sortHead('adds', 'Do košíku')}
              </th>
              <th className="text-right font-semibold px-4 py-2.5 w-32">
                {sortHead('rate', 'Poměr')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="text-center text-text-muted py-8">
                  Načítám…
                </td>
              </tr>
            )}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-text-muted py-8">
                  Žádné produkty.
                </td>
              </tr>
            )}
            {!loading &&
              visible.map((p) => {
                const malo = p.views < minViews;
                return (
                  <tr
                    key={p.key}
                    className={`border-b border-border-light ${malo ? 'text-text-muted' : ''}`}
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-3">
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt=""
                            loading="lazy"
                            className="h-9 w-9 shrink-0 object-contain rounded border border-border bg-white"
                          />
                        ) : (
                          <span className="h-9 w-9 shrink-0 rounded bg-border-light" />
                        )}
                        <div className="min-w-0">
                          {p.code ? (
                            <button
                              type="button"
                              onClick={() =>
                                p.code &&
                                setDetail({ code: p.code, name: p.name, image_url: p.image_url })
                              }
                              className="block max-w-full truncate text-left hover:underline hover:text-primary cursor-pointer"
                              title="Týdenní trend"
                            >
                              {p.name}
                            </button>
                          ) : (
                            <span className="block truncate">{p.name}</span>
                          )}
                          <span className="block text-xs text-text-muted font-mono">
                            {p.code}
                            {p.code && data?.recommended?.[p.code] && (
                              <span
                                className="ml-2 font-sans rounded bg-warning-light text-warning px-1.5 py-0.5"
                                title={`Nabízí se v ${data.recommended[p.code]} upsell nabídkách / sadách u jiných produktů — přidává se do košíku i bez zobrazení detailu, poměr je proto nadsazený.`}
                              >
                                i z nabídek
                              </span>
                            )}
                            {p.url && (
                              <a
                                href={p.url}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 hover:text-primary"
                                title="Otevřít na e-shopu"
                              >
                                ↗
                              </a>
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtNum(p.views)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtNum(p.adds)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">
                      {fmtPct(p.rate)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between px-5 py-3 text-xs text-text-secondary">
          <span>
            {fmtNum(rows.length)} produktů · strana {page + 1} / {pageCount}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 rounded border border-border disabled:opacity-40 cursor-pointer"
            >
              ←
            </button>
            <button
              type="button"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 rounded border border-border disabled:opacity-40 cursor-pointer"
            >
              →
            </button>
          </div>
        </div>
      )}
      {detail && (
        <CartRateTrendModal product={detail} eshop={eshop} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}
